/*
 * Klaviyo onsite JS, loaded ONLY after marketing consent. Spec: docs/cookie-consent-spec.md.
 *
 * WHY THIS EXISTS
 * ---------------
 * Klaviyo's Shopify *app embed* injects klaviyo.js directly into the page, outside Shopify's
 * Web Pixel sandbox, so Shopify's consent gate never reaches it. Measured on the live store
 * with consent still UNDECIDED (currentVisitorConsent() returning '' for every category):
 *
 *   Google    0 requests    correctly waiting
 *   Meta      0 requests    correctly waiting
 *   TikTok    0 requests    correctly waiting
 *   Klaviyo   24 on the homepage, 32 on a product page    NOT waiting
 *
 * Klaviyo's gating reacts to an explicit DECLINE but not to the undecided state, so it fires
 * in the window before the visitor has touched the banner. That is the one tracker on this
 * store that ignored consent, and in an EEA/GDPR shop it is the one that matters.
 *
 * So: the app embed is turned OFF (config/settings_data.json) and klaviyo.js is loaded from
 * here instead, only once marketing consent is actually granted.
 *
 * WHAT THIS PRESERVES
 * -------------------
 * Everything. Once consent is given this loads the identical script the app embed loaded, so
 * Active on Site, Viewed Product, Added to Cart, signup forms and popups all work exactly as
 * before, they just start after the visitor agrees instead of before. Klaviyo's server-side
 * Shopify sync (orders, customers, products) never depended on this script and is untouched.
 */
/*
 * SIGNUP POPUP HOLD (2026-10-19): removed the same day. The form WKFMzW now uses its own Klaviyo
 * display rules (exit intent, or from the 2nd page view; no time delay or scroll trigger; not shown
 * again after submit), set in the Klaviyo editor. See docs/cookie-consent-spec.md.
 */

(function () {
  'use strict';

  /* Klaviyo public company ID. Public by design: it ships in the script URL on every page. */
  var COMPANY_ID = 'TJDiSs';
  var SRC = 'https://static.klaviyo.com/onsite/js/' + COMPANY_ID + '/klaviyo.js?company_id=' + COMPANY_ID;
  var MARKER = 'kardima-klaviyo-onsite';

  var loaded = false;

  /* How long to give the wetracked app embed to inject its own klaviyo.js (it runs ~1 s after load). */
  var WETRACKED_GRACE_MS = 5000;

  function klaviyoOnPage() {
    return !!document.querySelector('script[src*="static.klaviyo.com/onsite/js/"][src*="klaviyo.js"]');
  }

  function loadKlaviyo() {
    if (loaded || document.getElementById(MARKER)) return;
    loaded = true;

    /*
     * Never load it twice (2026-10-18). Another app can inject klaviyo.js on its own (seen: the
     * WeTracked app embed loads static.klaviyo.com/onsite/js/klaviyo.js?company_id=... at page
     * load). If any copy is already on the page, Klaviyo is running and a second one only costs.
     */
    if (klaviyoOnPage()) return;

    /*
     * 2026-10-19 (tracking cleanup, kardima-business/2-ads/tracking-setup-2026-10.md): when this
     * ran first, wetracked still added its copy a moment later, so klaviyo.js loaded twice for
     * consenting visitors. wetracked is the primary tracker, so when its script is on the page we
     * give it a short grace period and only load our own copy if it never adds one.
     */
    if (document.querySelector('script[src*="pixel.wetracked.io"]')) {
      var waitedMs = 0;
      var poll = setInterval(function () {
        waitedMs += 250;
        if (klaviyoOnPage()) {
          clearInterval(poll);
        } else if (waitedMs >= WETRACKED_GRACE_MS) {
          clearInterval(poll);
          inject();
        }
      }, 250);
      return;
    }

    inject();
  }

  function inject() {
    if (document.getElementById(MARKER) || klaviyoOnPage()) return;
    var s = document.createElement('script');
    s.id = MARKER;
    s.async = true;
    s.src = SRC;
    document.head.appendChild(s);
  }

  /*
   * Marketing consent is the right gate, not analytics: Klaviyo's onsite script builds a
   * profile and drives advertising/marketing communication, which is what the marketing
   * category covers in the banner.
   */
  function hasMarketingConsent(api) {
    if (!api || typeof api.currentVisitorConsent !== 'function') return false;
    var c = api.currentVisitorConsent() || {};
    return c.marketing === 'yes';
  }

  function start(api) {
    if (hasMarketingConsent(api)) {
      loadKlaviyo();
      return;
    }

    /*
     * Not consented yet. Shopify fires `visitorConsentCollected` on document whenever
     * setTrackingConsent() resolves (including from our own banner), so the script loads the
     * moment the visitor accepts, with no page reload needed.
     */
    document.addEventListener('visitorConsentCollected', function (event) {
      var detail = (event && event.detail) || {};
      // The event payload uses booleans; fall back to re-reading the API if it is missing.
      if (detail.marketingAllowed === true || hasMarketingConsent(window.Shopify && window.Shopify.customerPrivacy)) {
        loadKlaviyo();
      }
    });
  }

  function init() {
    var shopify = window.Shopify;

    if (shopify && shopify.customerPrivacy) {
      start(shopify.customerPrivacy);
      return;
    }

    if (shopify && typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error || !window.Shopify || !window.Shopify.customerPrivacy) return;
        start(window.Shopify.customerPrivacy);
      });
      return;
    }

    /*
     * Shopify's own script has not run yet. Poll briefly, then give up, and give up QUIETLY
     * by loading nothing. For a tracker, failing closed is the correct direction: no data is
     * better than data collected without consent.
     */
    var waited = 0;
    var timer = setInterval(function () {
      waited += 100;
      if (window.Shopify && window.Shopify.customerPrivacy) {
        clearInterval(timer);
        start(window.Shopify.customerPrivacy);
      } else if (waited >= 6000) {
        clearInterval(timer);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
